import type { LanguageCode } from '@ampshift/shared';
import { createStore, readJson, safeStorage, writeJson } from '../store';
import type { UiLanguage } from '../i18n/strings';

/** Settings the backend has no field for; kept per browser in localStorage. */
export interface LocalSettings {
  uiLanguage: UiLanguage;
  /** 'auto' = Transcribe automatic language identification among the tenant's allowed languages. */
  inputLanguage: LanguageCode | 'auto';
  arabicVoiceId: 'Hala' | 'Zayd';
  englishVoiceId: string;
  /** Overrides the tenant push-to-talk combo when set. */
  pushToTalkShortcut?: string;
  /** Appearance: follow the OS, or force light/dark. */
  theme: Theme;
}

export type Theme = 'system' | 'light' | 'dark';

const KEY = 'ampshift.web.settings';

export const DEFAULT_LOCAL_SETTINGS: LocalSettings = {
  uiLanguage: 'en',
  inputLanguage: 'en-US',
  arabicVoiceId: 'Hala',
  englishVoiceId: 'Joanna',
  theme: 'system',
};

/** Amazon Polly neural en-US voices offered in the settings panel. */
export const ENGLISH_VOICES = ['Joanna', 'Matthew', 'Ruth', 'Stephen', 'Danielle', 'Gregory', 'Kendra', 'Kimberly', 'Salli', 'Joey', 'Ivy', 'Justin', 'Kevin'] as const;
export const ARABIC_VOICES = ['Hala', 'Zayd'] as const;

function load(): LocalSettings {
  const saved = readJson<Partial<LocalSettings>>(safeStorage('local'), KEY) ?? {};
  return { ...DEFAULT_LOCAL_SETTINGS, ...saved };
}

export const localSettingsStore = createStore<LocalSettings>(load());

export function updateLocalSettings(patch: Partial<LocalSettings>): void {
  localSettingsStore.set((prev) => {
    const next = { ...prev, ...patch };
    writeJson(safeStorage('local'), KEY, next);
    return next;
  });
}

/** Applies the UI language to the document: direction, lang attribute and Arabic font class. */
/** Applies the theme to <html data-theme> (removed for 'system') and keeps the browser chrome colour in sync. */
export function applyTheme(theme: Theme, doc: Document = document): void {
  if (theme === 'system') delete doc.documentElement.dataset['theme'];
  else doc.documentElement.dataset['theme'] = theme;
  const dark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#161d29' : '#1b67f1');
}

export function applyDocumentLanguage(lang: UiLanguage, doc: Document = document): void {
  const root = doc.documentElement;
  root.lang = lang;
  root.dir = lang === 'ar' ? 'rtl' : 'ltr';
  root.classList.toggle('lang-ar', lang === 'ar');
}

export function uiLanguageToLocale(lang: UiLanguage, arabicDialect: LanguageCode = 'ar-AE'): LanguageCode {
  return lang === 'ar' ? (arabicDialect.startsWith('ar') ? arabicDialect : 'ar-AE') : 'en-US';
}
